import { HttpClient, HttpErrorResponse, HttpEventType, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DialogService, ErrorLevels } from '../modules/shared/services/dialog.service';
import { ApiResultValues, IApiQueryResult, IApiResult } from '../modules/shared/models/apiResultModels';
import { BehaviorSubject, Observable,  Subject, catchError,  firstValueFrom, map, of } from 'rxjs';
import { AppConfig } from '../modules/shared/models/app-config';
import { billingOrganisation } from '../modules/shared/models/organisationModels';
import { CompanyPostingOptions, ConnectionRequestObject, ExternalDataSource, ProviderConnectRequest } from '../modules/shared/models/nt-company';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
private baseUrl: string = "";

    constructor(private http: HttpClient,
        private dbx: DialogService) {
        this.baseUrl = AppConfig.settings.apiFar;
        console.log("ApiService", "Constructor");
     }

    private getMapQueryFunction = (msg: string) => {
        return map((res: IApiQueryResult) => {
            if(res.result !== ApiResultValues.Ok){
                if(res.result === ApiResultValues.ExternalDataError && (res.externalDataResult === 'AuthError' || res.externalDataResult === 'RefreshError')){
                    //This condition requires possible EDM reconnect.  Create an event for consumer to handle
                    this.dbx.reportError(ErrorLevels.LogOnly,
                        "External data error observed: " + msg,
                        res.message,
                        res.result,
                        res.error,
                        res.count
                    );
                    this.ExternalDataReconnectRequired.next(true);

                }else{  
                    this.dbx.reportError(ErrorLevels.Critical,
                        msg,
                        res.message,
                        res.result,
                        res.error,
                        res.count);
                    this.ExternalDataReconnectRequired.next(false);
                }
                
            }else{
                this.ExternalDataReconnectRequired.next(false);
            }
            return res;
        })
    };

    private getMapFunction = (msg: string) => {
        return map((res: IApiResult) => {
                if(res.result !== ApiResultValues.Ok){
                    if(res.result === ApiResultValues.ExternalDataError && (res.externalDataResult === 'AuthError' || res.externalDataResult === 'RefreshError')){
                    //This condition requires possible EDM reconnect.  Create an event for consumer to handle
                        this.dbx.reportError(ErrorLevels.LogOnly,
                            "External data error observed: " + msg,
                            res.message,
                            res.result,
                            res.error
                        );
                        this.ExternalDataReconnectRequired.next(true);
                    }else{  
                        this.dbx.reportError(ErrorLevels.Critical,
                            msg,
                            res.message,
                            res.result,
                            res.error);
                        this.ExternalDataReconnectRequired.next(false);
                    }
                }else{
                    this.ExternalDataReconnectRequired.next(false);
                }
                return res;
        });
    }

    ExternalDataReconnectRequired = new BehaviorSubject<boolean>(false);

    //Where we know that the API will be returning data not conforming to our V2 principles,
    //  use this map function to convert the data.  Consume will have to determine success of
    //  result object.
    getV1MapFunction = () => {
        return map((res: any): IApiResult => {
            return {
                result: ApiResultValues.Ok,
                data: res,
                message: 'Result converted to V2 result'
            }
        });
    }

    private getV1MapQueryFunction = () => {
        return map((res: any): IApiQueryResult => {
            return {
                result: ApiResultValues.Ok,
                data: res,
                message: 'Result converted to V2 result',
                count: res.length
            }
        });
    }

    getCatchErrorFunction = (msg: string) => {
        return catchError(e => {
            this.handlePrimaryError(e, msg, false);
            return of<any>({
                result: ApiResultValues.Exception,
                message: 'Unknown API Error',
                data: []
            });
        });
    }

    private getCatchErrorSilentFunction = (msg: string) => {
        return catchError(e => {
            this.handlePrimaryError(e, msg, true);
            return of<any>({
                result: ApiResultValues.Exception,
                message: 'Unknown API Error',
                data: []
            });
        });
    }

    private getCatchErrorQueryFunction = (msg: string) => {
        return catchError((e: any) => {
            this.handlePrimaryError(e, msg, false);
            const retObj: IApiQueryResult = {
                result: ApiResultValues.Exception,
                message: 'Unknown API Error',
                data: [],
                count: 0
            };
            return of<any>(retObj);
        });
    }

    private handlePrimaryError = (e: any, msg: string, silent: boolean)  => {
        if(!e){
            this.dbx.reportError(ErrorLevels.LogOnly,
                msg,
                'An error has been thrown but no error information was provided',
            );
            return;
        }
        if(e instanceof HttpErrorResponse){
            if(e.status === 401){
                //We don't do a UI prompt for this error...
                this.dbx.reportError(ErrorLevels.Warning,
                    msg,
                    "API reported unauthorised request",
                    ApiResultValues.Exception,
                    e);
                return;
            }else if(e.status === 404){
                this.dbx.reportError(ErrorLevels.Critical,
                    msg,
                    "API reported URL not found",
                    ApiResultValues.Exception,
                    e);
                return;
            }else if(e.status === 504){
                this.dbx.reportError(ErrorLevels.Critical,
                    "Your prcoessing request has timed out.  If you have requested to process a period or financial year end, it is likely that the server is busy completing your request and it is important that you do not request a second time.  Please allow 1 hour for the processing to complete before refreshing your company.  If processing has not completed after this time, please contact support.",
                    "Api request has resulted in gateway time-out.  Please do not re-request",
                    e
                )
                return;
            }else if(e.status === 587){
                this.dbx.reportError(ErrorLevels.Critical,
                    "Unfortunately, the subscription that this company is associated with has been suspended or terminated.  Please contact support for further assistance.",
                    "Api request has resulted in a 587 error.  Please contact support",
                    e
                );
                return;
            }
        }else if(e.error){
            if(e.error === 'InvalidRefreshToken'){
                //This is failure to refresh auth and not an api failure
                this.dbx.reportError(ErrorLevels.Warning,
                    msg,
                    "Attempt to refresh token has failed",
                    ApiResultValues.Exception,
                    e
                );
                return;
            }else if(e.error === 'UnAuthorised'){
                this.dbx.reportError(ErrorLevels.Warning,
                    msg,
                    "API reported unauthorised request",
                    ApiResultValues.Exception,
                    e);
                return;
            }
        } 

        const level = silent ? ErrorLevels.Warning : ErrorLevels.Critical;
        
        this.dbx.reportError(level,
            msg,
            "Unknown API Error",
            ApiResultValues.Exception,
            e);
    }

    downloadExcelSpreadsheet(url: string, linkHref: string, mimeType?: string) {
        const mime = mimeType ? mimeType : 'application/ms-excel';

        return new Observable<boolean>(o => {
            this.http.get(url, { responseType: 'arraybuffer' }).pipe(
                this.getCatchErrorFunction("An error occurred attempted to download the file")
            ).subscribe(resp => {
                const b = new Blob([resp], { type: mime });
                if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
                    (window.navigator as any).msSaveOrOpenBlob(b);
                    return;
                }
        
                // For other browsers: 
                // Create a link pointing to the ObjectURL containing the blob.
                const data = window.URL.createObjectURL(b);
        
                const link = document.createElement('a');
                link.href = data;
                link.download = linkHref;
                // this is necessary as link.click() does not work on the latest firefox
                link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
        
                setTimeout(function () {
                    // For Firefox it is necessary to delay revoking the ObjectURL
                    window.URL.revokeObjectURL(data);
                    o.next(true);
                    link.remove();
                }, 100);
            });
        });
    }

    downloadExcelSpreadsheetPost(url: string, data: any, linkHref: string, mimeType?: string) {
        const mime = mimeType ? mimeType : 'application/ms-excel';

        return new Observable<boolean>(o => {
            this.http.post(url, data, { responseType: 'arraybuffer' , observe: 'response'}).pipe(
                this.getCatchErrorFunction("An error occurred attempted to download the file")
            ).subscribe(resp => {
                let body: ArrayBuffer;

                if(resp instanceof HttpResponse && resp.type === HttpEventType.Response){
                    //We have a response object
                    if(resp.body instanceof ArrayBuffer){
                        //We have a byte array
                        body = resp.body;
                    }else{
                        //We have an error as otherwise resp is byte[]
                        o.next(false);
                        return;
                    }
                }else if(resp.result !== ApiResultValues.Ok){
                        o.next(false);
                        return;
                }

                const b = new Blob([body], { type: mime });
                if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
                    (window.navigator as any).msSaveOrOpenBlob(b);
                    return;
                }

                const header = resp.headers.get('Content-Disposition');
        
                // For other browsers: 
                // Create a link pointing to the ObjectURL containing the blob.
                const data = window.URL.createObjectURL(b);
        
                const link = document.createElement('a');
                link.href = data;
                link.download = header ?? linkHref;
                // this is necessary as link.click() does not work on the latest firefox
                link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
        
                setTimeout(function () {
                    // For Firefox it is necessary to delay revoking the ObjectURL
                    window.URL.revokeObjectURL(data);
                    o.next(true);
                    link.remove();
                }, 100);
            });
        });
    }

    clearConnectRequest() {
        localStorage.removeItem("connectionRequestObject-QuickBooks");
        localStorage.removeItem("connectionRequestObject-Xero");
        localStorage.removeItem("connectionRequestObject-SageCloud");
        localStorage.removeItem("externalDataSource");
        localStorage.removeItem(AppConfig.settings.oAuthCookieName);
    }

    getCachedConnectRequest(provider: string): ConnectionRequestObject {
        return JSON.parse(localStorage.getItem("connectionRequestObject-" + provider));
    }
    
    getCachedDataSource(): ExternalDataSource {
        const ds = JSON.parse(localStorage.getItem("externalDataSource"));
        this.clearCachedDataSource();
        return ds;
    }
    
    clearCachedDataSource() {
        localStorage.removeItem("externalDataSource");
    }

    //Places the selected connection datasource into local storage for later use
    //Used by company/connect components
    saveSelectedDatasource(ds: ExternalDataSource) {
        localStorage.setItem("externalDataSource", JSON.stringify(ds));
    }  

    //Used to verify the guid used to make the provider connectiong request
    //Used by company/connect components
    async getConnectState(): Promise<ProviderConnectRequest> {
        let localStorageItem = localStorage.getItem(AppConfig.settings.oAuthCookieName);
        const requestObj: ProviderConnectRequest = JSON.parse(localStorageItem);

        if(localStorageItem){
            localStorage.removeItem(AppConfig.settings.oAuthCookieName);
            return requestObj;
        }

        return null;

        // const uri = `${AppConfig.settings.apiNettTracker}company/getState`;
        // const result = await firstValueFrom(this.http.get<any>(uri)
        //     .pipe(
        //         this.getMapFunction("There was an error while attempting ascertain the connection process state"),
        //         this.getCatchErrorFunction("There was an error while attempting ascertain the connection process state")
        //     )) as IApiResult;
        // if(result.result === ApiResultValues.Ok){
        //     return {
        //         state: result.data,
        //         url: '',
        //         purpose: 'Connect',
        //         providerName: ''
        //     };
        // }else{
        //     return null;
        // }
        
    }

    async getApiExplorerRequest(companyId: string, request: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/company/api`;
        return firstValueFrom(this.http.post<IApiResult>(url, request)
            .pipe(
                this.getMapFunction("There was an error calling the api method on this company"),
                this.getCatchErrorFunction("There was an error calling the api method on this company")
            )
        );
    }

    async modifyFinancialYear(proposedYearEnd: string, preflight: boolean, companyId: string): Promise<IApiResult>{
        const url: string = `${AppConfig.settings.apiFar}${companyId}/financialYear/modify`;
        const payload = {
            preflight,
            proposedYearEnd
        };
        return firstValueFrom(this.http.post<IApiResult>(url, payload)
            .pipe(
                this.getMapFunction("There was an error calling the modify financial year function"),
                this.getCatchErrorFunction("There was an error calling the modify financial year function")
            )
        );
      }

    async createCompany(model: any): Promise<IApiResult> {
        const uri = `${AppConfig.settings.apiNettTracker}company/create`;
        return await firstValueFrom(this.http.post<any>(uri, model).pipe(
            this.getMapFunction("There was an error creating the new company"),
            this.getCatchErrorFunction("There was an error creating the new company")
        ));
    }

    async getUserCompanyList(): Promise<IApiResult> {
        return await firstValueFrom(this.http.get<IApiResult>(`${AppConfig.settings.apiValidateUrl}validate`)
            .pipe(
                this.getMapFunction("There was an error loading the company list"),
                this.getCatchErrorFunction("There was an error loading the company list")
            )
        );
    }

    async getApiVersion(): Promise<IApiResult> {
        return await firstValueFrom(this.http.get<IApiResult>(`${AppConfig.settings.apiNettTracker}company/version`)
            .pipe(
                this.getMapFunction("There was an error obtaining the API version"),
                this.getCatchErrorFunction("There was an error obtaining the API version")
            )
        );
    }

    async getBillingOrganisation(id: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFalServices}${id}/Organisation`;
        return await firstValueFrom( this.http.get<billingOrganisation>(url).pipe(
                this.getV1MapFunction(),
                this.getCatchErrorFunction("There was an error retrieving the billing organisation")
            )
        );
    }

    async getInvoices(id: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFalServices}${id}/Invoice`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
            this.getV1MapFunction(),
            this.getCatchErrorFunction("There was an error retrieving invoices")
        ));
        
    }
    
    downloadInvoice(id: number, org:string) {
        const url = `${AppConfig.settings.apiFalServices}${org}/Invoice/${id}`;
        const linkHref = `invoice${id}.pdf`;

        return this.downloadExcelSpreadsheet(url, linkHref);

    }

    async getUsers(id: string): Promise<IApiResult>{
        const url = `${AppConfig.settings.apiFalServices}${id}/User`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
            this.getV1MapFunction(),
            this.getCatchErrorFunction("There was an error retrieving organisation users")
        ));
    }

    async createOrgUser(user: any, id: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFalServices}${id}/User/Create`;
        return await firstValueFrom(this.http.post<any>(url, user).pipe(
            this.getV1MapFunction(),
            this.getCatchErrorFunction("An error occurred attempting to create organisation user")
        ));
    }
    
    async updateOrgUser(user, id: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFalServices}${id}/User/Update`;
        return await firstValueFrom(this.http.post<any>(url, user).pipe(
            this.getV1MapFunction(),
            this.getCatchErrorFunction("An error occurred attempting to update organisation user")
        ));
    }

    async updateOrgUserPassword(user, id: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFalServices}${id}/User/UpdatePassword`;
        return await firstValueFrom(this.http.post<any>(url, user).pipe(
            this.getV1MapFunction(),
            this.getCatchErrorFunction("An error occurred attempting to update organisation user")
        ));
    }
    
    async deleteOrgUser(userId: string, id: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFalServices}${id}/User/Delete/${userId}`;
        return await firstValueFrom(this.http.post<boolean>(url, null).pipe(
            this.getV1MapFunction(),
            this.getCatchErrorFunction("An error occurred attempting to delete organisation user")
        ));
    }

    async getOrganisationUserCompanies(orgId: string, userId: string): Promise<IApiQueryResult> {
        const url = `${AppConfig.settings.apiNettTracker}Company/Organisation/${orgId}/${userId}`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
            this.getMapQueryFunction('An error occurred attempting to get org user company roles'),
            this.getCatchErrorQueryFunction("An error occurred attempting to get org user company roles")
        ));
    }
    
    async updateOrgUserCompanies(orgId: string, userCompanies: any): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiNettTracker}Company/Organisation/${orgId}`;
        return await firstValueFrom(this.http.post<any>(url, userCompanies).pipe(
            this.getMapFunction('An error occurred attempting to update org user company roles'),
            this.getCatchErrorFunction("An error occurred attempting to update org user company roles")
        ));
    }

    async getServiceOfferings(): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiSignupUrl}getServiceOfferings/FixedAssetRegister`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getV1MapFunction(),
           this.getCatchErrorFunction("An error occurred while retrieving the current service offerings") 
        ));
    }

    async getPaymentMethods(id: string): Promise<IApiResult> {
        let url: string = `${AppConfig.settings.apiFalServices}${id}/PaymentMethod/list`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
            this.getV1MapFunction(),
            this.getCatchErrorFunction("An error occurred attempting to get your payment methods")
        ));
    }
    
    async createPaymentMethod(id: string, provider: string): Promise<IApiResult> {
        let url: string = `${AppConfig.settings.apiFalServices}${id}/PaymentMethod/create/${provider}`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
            this.getV1MapFunction(),
            this.getCatchErrorFunction("An error occurred attempting to create your payment method")
        ));
    }

    async updateServicePayentMethod(id: string, serviceId: string, paymentMethodId: number): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFalServices}${id}/Subscriptions/updatePayment/${serviceId}/${paymentMethodId}`;
        return await firstValueFrom(this.http.get<any>(url)
            .pipe(
                this.getV1MapFunction(),
                this.getCatchErrorFunction("An error occurred attempting to update the payment method for this service.")
            ));

    }

    async createSignupPaymentRequest(id: string, provider: string): Promise<IApiResult> {
        const model = {
            organisationId: id,
            provider: provider
        };
        const url = `${AppConfig.settings.apiSignupUrl}CreatePaymentRequest`;
        return await firstValueFrom(this.http.post<any>(url, model).pipe(
            this.getV1MapFunction(),
            this.getCatchErrorFunction("An error occurred trying to create your payment request.")
        ));
    }
      
    async completeSignupPaymentRequest(method: any): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiSignupUrl}CompletePaymentRequest`;
        return await firstValueFrom(this.http.post<any>(url, method).pipe(
            this.getV1MapFunction(),
            this.getCatchErrorFunction("An error occurred trying to complete your payment request.")
        ));
    }
    
    async completePaymentMethod(id: string, method: any): Promise<IApiResult> {
        let url: string = `${AppConfig.settings.apiFalServices}${id}/PaymentMethod/complete`;
        return await firstValueFrom(this.http.post<any>(url, method).pipe(
            this.getV1MapFunction(),
            this.getCatchErrorFunction("An error occurred attempting to complete your payment method")
        ));
    }
    
    async deletePaymentMethod(id: string, methodId: number): Promise<IApiResult> {
        let url: string = `${AppConfig.settings.apiFalServices}${id}/PaymentMethod/delete/${methodId}`;
        return await firstValueFrom(this.http.post<boolean>(url, null).pipe(
            this.getV1MapFunction(),
            this.getCatchErrorFunction("An error occurred attempting to delete your payment method")
        ));
    }

    downloadAssetImportTemplate(companyId: string){
        const url = `${AppConfig.settings.apiFar}${companyId}/asset/template`;
        const linkHref = "assetimportTemplate.xlsx";
        return this.downloadExcelSpreadsheet(url, linkHref);
    }

    downloadAccrualPrepaymentTemplate(companyId: string, type: string){
        const url = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment/template/${type}`;
        const linkHref = "accrualPrepayDefIncImportTemplate.xlsx";
        return this.downloadExcelSpreadsheet(url, linkHref);
    }

    async updateCompany(company: any, companyId: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/company/update`;
        return await firstValueFrom(this.http.post<any>(url, company).pipe(
            this.getMapFunction("An error occurred updating the company details"),
            this.getCatchErrorFunction("An error occurred updating the company details")
        ));
    }

    async createConnectionRequest(provider: string, purpose: string, companyId?: string): Promise<IApiResult>{
        const uri = `${AppConfig.settings.apiNettTracker}company/createRequest/${provider}`;
        return await firstValueFrom(this.http.get<IApiResult>(uri).pipe(
            this.getMapFunction("An error occurred building the request to connect to provider"),
            this.getCatchErrorFunction("An error occurred building the request to connect to provider")
        ));
    }

    async completeConnectionRequest(values: any[]): Promise<IApiQueryResult>{
        const uri = `${AppConfig.settings.apiNettTracker}company/connect`;
        const result = await firstValueFrom(this.http.post<any>(uri, values).pipe(
            this.getMapQueryFunction("There was an error connecting the company to external datasource"),
            this.getCatchErrorQueryFunction("There was an error connecting the company to external datasource")
        )) as IApiQueryResult;

        if(result.result === ApiResultValues.Ok){
            this.clearConnectRequest();
        }

        return result;
    }

    async createReconnectionRequest(companyId: string, provider: string): Promise<IApiResult>{
        this.clearConnectRequest();
        const uri = `${AppConfig.settings.apiFar}${companyId}/reconnect/RequestReconnect`;
        return await firstValueFrom(this.http.get<any>(uri).pipe(
            this.getMapFunction("An error occurred building the request to connect to provider"),
            this.getCatchErrorFunction("An error occurred building the request to connect to provider")
        ));

        
    }
    async completeReconnectionRequest(companyId: string, values: any[]): Promise<IApiResult> {
        let uri = `${AppConfig.settings.apiFar}${companyId}/reconnect/completeReconnect`;
        const result = await firstValueFrom(this.http.post<any>(uri, values).pipe(
            this.getMapFunction("There was an error reconnecting the company to external datasource"),
            this.getCatchErrorFunction("There was an error reconnecting the company to external datasource")
        )) as IApiResult;

        if(result.result === ApiResultValues.Ok){
            this.clearConnectRequest();
        }

        return result;
    }

    async changeCompanyProvider(companyId: string, ds: ExternalDataSource, newRegion: string): Promise<IApiResult>{
        const uri = `${AppConfig.settings.apiFar}${companyId}/company/changeProvider`;
        const payload = {
            region: newRegion,
            externalDataSource: ds
        };
        return await firstValueFrom(this.http.post<any>(uri, payload).pipe(
            this.getMapFunction("There was an error completeing the change of company provider"),
            this.getCatchErrorFunction("There was an error completeing the change of company provider")
        ));
    }

    async getExternalCompany(dataSource: ExternalDataSource): Promise<IApiResult> {
        const uri = `${AppConfig.settings.apiNettTracker}company/getExternal`;
        return await firstValueFrom(this.http.post<any>(uri, dataSource).pipe(
            this.getMapFunction("There was an error retrieving the external company data"),
            this.getCatchErrorFunction("There was an error retrieving the external company data")
        ));
      }

    async getConnectionStatus(companyId: string): Promise<IApiResult> {
        const uri = `${AppConfig.settings.apiFar}${companyId}/company/status`;
        return await firstValueFrom(this.http.get<IApiResult>(uri).pipe(
            this.getMapFunction("An error occurred retrieving the company connection status"),
            this.getCatchErrorFunction("An error occurred retrieving the company connection status")
        ));
    }

    async scheduleCompanyDeletion(companyId: string): Promise<IApiResult> {
        let uri = `${AppConfig.settings.apiFar}${companyId}/company/delete`;
        return await firstValueFrom(this.http.post<IApiResult>(uri, null).pipe(
            this.getMapFunction("An error has occurred while scheduling the company for deletion"),
            this.getCatchErrorFunction("An error has occurred while scheduling the company for deletion")
        ));
    }

    async cancelCompanyDeletion(companyId: string): Promise<IApiResult> {
        let uri = `${AppConfig.settings.apiFar}${companyId}/company/cancelDelete`;
        return await firstValueFrom(this.http.post<IApiResult>(uri, null).pipe(
            this.getMapFunction("An error has occurred while cancelling the scheduled deletion"),
            this.getCatchErrorFunction("An error has occurred while cancelling the scheduled deletion")
        ));
    }

    async resetCompany(yearStart: Date, yearEnd: Date, currency: string, mode: string, companyId: string): Promise<IApiResult>{
        const url = `${AppConfig.settings.apiFar}${companyId}/company/reset`;
        const payload = {
            yearStart,
            yearEnd,
            currency,
            mode
        };

        return await firstValueFrom(this.http.post<IApiResult>(url, payload).pipe(
            this.getMapFunction("An error occurred while resetting this company"),
            this.getCatchErrorFunction("An error occurred while resetting this company")
        ));
    }

    async disconnectCompany(companyId: string): Promise<IApiResult> {
        const uri = `${AppConfig.settings.apiFar}${companyId}/company/disconnect`;
        return await firstValueFrom(this.http.post<IApiResult>(uri, null)
            .pipe(
                this.getMapFunction("An error occurred while disconnecting this company from the datasource"),
                this.getCatchErrorFunction("An error occurred while disconnect this company from the data source")
            )
        );
    }

    async removeNtUser(userId: string, companyId: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/company/removeUser/${userId}`;
        return await firstValueFrom(this.http.post<IApiResult>(url, null)
            .pipe(
                this.getMapFunction("There was an error removing the user from the company"),
                this.getCatchErrorFunction("There was an error removing the user from the company")
            )
        );
    }
    
    async updateNtUserRole(userId: string, role: string, companyId: string): Promise<IApiResult> {
        let url: string = `${AppConfig.settings.apiFar}${companyId}/company/updateUserRole/${userId}/${role}`;
        return await firstValueFrom(this.http.post<IApiResult>(url, null)
            .pipe(
                this.getMapFunction("There was an error updating the company user properties"),
                this.getCatchErrorFunction("There was an error updating the company user properties")
            )
        );
    }
    
    async createNtUser(user: any, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/company/createUserRole`;
        return await firstValueFrom(this.http.post<IApiResult>(url, user)
            .pipe(
                this.getMapFunction("There was an error creating the new company user"),
                this.getCatchErrorFunction("There was an error creating the new company user")
            )
        );
    }

    async enableCompanyFeature(feature: string, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/company/enableFeature/${feature}`;
        return await firstValueFrom(this.http.get<IApiResult>(url)
            .pipe(
                this.getMapFunction("There was an error enabling the requested feature"),
                this.getCatchErrorFunction("There was an error enabling the requested feature")
            )
        );
    }

    async disableCompanyFeature(feature: string, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/company/disableFeature/${feature}`;
        return await firstValueFrom(this.http.get<IApiResult>(url)
            .pipe(
                this.getMapFunction("There was an error disabling the requested feature"),
                this.getCatchErrorFunction("There was an error disabling the requested feature")
            )
        );
    }

    async getCompanyFeatures(companyId: string): Promise<IApiQueryResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/company/features`;
        return await firstValueFrom(this.http.get<IApiQueryResult>(url)
            .pipe(
                this.getMapFunction("There was an error retrieving the feature list"),
                this.getCatchErrorFunction("There was an error retrieving the feature list")
            )
        );
    }

    async getCompanyProviderTrackingData(companyId: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/company/ProviderTrackingData`;
        return await firstValueFrom( this.http.get<any>(url)
            .pipe(
                this.getMapFunction("There was an error getting the company ProviderTrackingData"),
                this.getCatchErrorFunction("There was an error getting the company ProviderTrackingData")
        ));
    }

    async getCompanySummary(companyId: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/company/Summary`;
        return await firstValueFrom( this.http.get<any>(url)
            .pipe(
                this.getMapFunction("There was an error getting the company summary"),
                this.getCatchErrorFunction("There was an error getting the company summary")
        ));
    }

    async getCompanySummaryRefresh(companyId: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/company/SummaryRefresh`;
        return await firstValueFrom( this.http.get<any>(url)
            .pipe(
                this.getMapFunction("There was an error getting the company summary"),
                this.getCatchErrorFunction("There was an error getting the company summary")
        ));
    }

    async migrateCompanyToV2(enableAssetTaxDepreciation: boolean, enablePerpetualFinancialYearEnd: boolean, companyId: string): Promise<IApiResult> {
        const data = {
            enableAssetTaxDepreciation,
            enablePerpetualFinancialYearEnd
        }
        const url = `${AppConfig.settings.apiFar}${companyId}/company/MigrateToVersion2`;
        return await firstValueFrom( this.http.post<any>(url, data)
            .pipe(
                this.getMapFunction("There was an error migrating the company to V2"),
                this.getCatchErrorFunction("There was an error migrating the company to V2")
        ));
    }

    async getAssetGroups(companyId: string): Promise<IApiQueryResult>{
        let url: string = `${AppConfig.settings.apiFar}${companyId}/assetGroup/`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapQueryFunction("An error occurred requesting the asset groups"),
           this.getCatchErrorQueryFunction("An error occurred requesting the asset groups") 
        ));
    }

    async getAssetGroup(id: number, companyId: string): Promise<IApiQueryResult>{
        let url: string = `${AppConfig.settings.apiFar}${companyId}/assetGroup/${id}`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapQueryFunction("An error occurred requesting the asset group"),
           this.getCatchErrorQueryFunction("An error occurred requesting the asset group") 
        ));
    }

    async deleteAssetGroup(id: number, companyId: string): Promise<IApiQueryResult>{
        let url: string = `${AppConfig.settings.apiFar}${companyId}/assetGroup/${id}`;
        return await firstValueFrom(this.http.delete<any>(url).pipe(
           this.getMapQueryFunction("An error occurred deleting the asset group"),
           this.getCatchErrorQueryFunction("An error occurred deleting the asset group") 
        ));
    }

    async updateAssetGroup(id: number, data: any, companyId: string): Promise<IApiQueryResult>{
        let url: string = `${AppConfig.settings.apiFar}${companyId}/assetGroup/${id}`;
        return await firstValueFrom(this.http.post<any>(url, data).pipe(
           this.getMapQueryFunction("An error occurred deleting the asset group"),
           this.getCatchErrorQueryFunction("An error occurred deleting the asset group") 
        ));
    }

    async getAccrualPrepaymentGroups(companyId: string): Promise<IApiQueryResult>{
        let url: string = `${AppConfig.settings.apiFar}${companyId}/accrualPrepaymentGroup/`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapQueryFunction("An error occurred requesting the accrual groups"),
           this.getCatchErrorQueryFunction("An error occurred requesting the accrual groups") 
        ));
    }

    async getAccrualPrepaymentGroup(id: number, companyId: string): Promise<IApiQueryResult>{
        let url: string = `${AppConfig.settings.apiFar}${companyId}/accrualPrepaymentGroup/${id}`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapQueryFunction("An error occurred requesting the accrual group"),
           this.getCatchErrorQueryFunction("An error occurred requesting the accrual group") 
        ));
    }

    async deleteAccrualPrepaymentGroup(id: number, companyId: string): Promise<IApiQueryResult>{
        let url: string = `${AppConfig.settings.apiFar}${companyId}/accrualPrepaymentGroup/${id}`;
        return await firstValueFrom(this.http.delete<any>(url).pipe(
           this.getMapQueryFunction("An error occurred deleting the accrual group"),
           this.getCatchErrorQueryFunction("An error occurred deleting the accrual group") 
        ));
    }

    async updateAccrualPrepaymentGroup(id: number, data: any, companyId: string): Promise<IApiQueryResult>{
        let url: string = `${AppConfig.settings.apiFar}${companyId}/accrualPrepaymentGroup/${id}`;
        return await firstValueFrom(this.http.post<any>(url, data).pipe(
           this.getMapQueryFunction("An error occurred updating the accrual group"),
           this.getCatchErrorQueryFunction("An error occurred updating the accrual group") 
        ));
    }

    async createAccrualPrepaymentItem(data: any, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment`;
        return await firstValueFrom(this.http.put<any>(url, data).pipe(
           this.getMapQueryFunction("An error occurred creating the accrual prepayment item"),
           this.getCatchErrorQueryFunction("An error occurred creating the accrual prepayment item") 
        ));
    }

    async updateAccrualPrepaymentItem(id: number, data: any, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment/${id}`;
        return await firstValueFrom(this.http.post<any>(url, data).pipe(
           this.getMapQueryFunction("An error occurred updating the accrual prepayment item"),
           this.getCatchErrorQueryFunction("An error occurred updating the accrual prepayment item") 
        ));
    }

    async getAccrualPrepaymentItem(id: number, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment/${id}`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapQueryFunction("An error occurred retrieving the accrual prepayment item"),
           this.getCatchErrorQueryFunction("An error occurred retrieving the accrual prepayment item") 
        ));
    }

    async getAccrualPrepaymentEntity(id: number, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment/entity/${id}`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapQueryFunction("An error occurred retrieving the accrual prepayment entity"),
           this.getCatchErrorQueryFunction("An error occurred retrieving the accrual prepayment entity") 
        ));
    }

    async getAccrualPrepaymentItemForTransaction(id: number, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment/transaction/${id}`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapQueryFunction("An error occurred retrieving the accrual prepayment item"),
           this.getCatchErrorQueryFunction("An error occurred retrieving the accrual prepayment item") 
        ));
    }

    async getAccrualPrepaymentItems(companyId: string): Promise<IApiQueryResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapQueryFunction("An error occurred retrieving the accrual prepayment items"),
           this.getCatchErrorQueryFunction("An error occurred retrieving the accrual prepayment items") 
        ));
    }

    async getAccrualPrepaymentItemsDeleted(companyId: string): Promise<IApiQueryResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment/deleted`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapQueryFunction("An error occurred retrieving the deleted accrual prepayment items"),
           this.getCatchErrorQueryFunction("An error occurred retrieving the deleted accrual prepayment items") 
        ));
    }

     async getBuildPrepaymentForTransaction(id: number, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/transaction/build/prepayment/${id}`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapFunction("An error occurred building the prepayment from the transaction"),
           this.getCatchErrorFunction("An error occurred building the prepayment from the transaction") 
        ));
    }

    async createPrepaymentForTransaction(id: number, prepayment: any, companyId: string):Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/transaction/prepayment/${id}`;
        return await firstValueFrom(this.http.post<any>(url, prepayment).pipe(
            this.getMapFunction("An error occurred creating the prepayment from the transaction"),
            this.getCatchErrorFunction("An error occurred creating the prepayment from the transaction")
        ));
    }

    async accrualPrepaymentBulkNominalUpdate(items: any[], companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment/nominal`;
        return await firstValueFrom(this.http.post<any>(url, items).pipe(
           this.getMapQueryFunction("An error occurred bulk nominal updating accrual prepayments"),
           this.getCatchErrorQueryFunction("An error occurred bulk nominal updating accrual prepayments") 
        ));
    }

    async getAccrualPrepaymentStatement(data: any, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment/statement`;
        return await firstValueFrom(this.http.post<any>(url, data).pipe(
           this.getMapFunction("An error occurred retrieving the accrual prepayment statement"),
           this.getCatchErrorFunction("An error occurred retrieving the accrual prepayment statement") 
        ));
    }

    async getAccrualPrepaymentProjection(data: any, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment/projection`;
        return await firstValueFrom(this.http.post<any>(url, data).pipe(
           this.getMapFunction("An error occurred retrieving the accrual prepayment projection"),
           this.getCatchErrorFunction("An error occurred retrieving the accrual prepayment projection") 
        ));
    }

    async deleteAccrualPrepaymentItem(model: any, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment/delete`;
        return await firstValueFrom(this.http.post<any>(url, model).pipe(
           this.getMapQueryFunction("An error occurred deleting the accrual prepayment"),
           this.getCatchErrorQueryFunction("An error occurred deleting the accrual prepayment") 
        ));
    }

    async deleteAccrualPrepaymentReverse(accrualId: number, companyId: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment/deleteReverse/${accrualId}`;
        return await firstValueFrom(this.http.post<any>(url, null).pipe(
            this.getMapFunction("An error occurred deleting this accrual/prepayment"),
            this.getCatchErrorFunction("An error occurred deleting this accrual/prepayment")
        ));
    }

    async accrualPrepaymentPostRelease(model: any, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment/release`;
        return await firstValueFrom(this.http.post<any>(url, model).pipe(
           this.getMapQueryFunction("An error occurred releasing the accrual prepayment"),
           this.getCatchErrorQueryFunction("An error occurred releasing the accrual prepayment") 
        ));
    }

    async accrualPrepaymentPostAccrual(model: any, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment/accrue`;
        return await firstValueFrom(this.http.post<any>(url, model).pipe(
           this.getMapQueryFunction("An error occurred accruing the accrual prepayment"),
           this.getCatchErrorQueryFunction("An error occurred accruing the accrual prepayment") 
        ));
    }

    async updateAccrualPrepaymentPosting(id: number, postingDate: string, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment/updatePosting/${id}`;
        return await firstValueFrom(this.http.post<any>(url, { id, postingDate }).pipe(
           this.getMapQueryFunction("An error occurred accruing the accrual prepayment"),
           this.getCatchErrorQueryFunction("An error occurred accruing the accrual prepayment") 
        ));
    }

    async deleteAccrualPrepaymentPosting(postingId: number, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment/deletePosting/${postingId}`;
        return await firstValueFrom(this.http.post<any>(url, null).pipe(
           this.getMapQueryFunction("An error occurred deleting the accrual prepayment posting"),
           this.getCatchErrorQueryFunction("An error occurred deleting the accrual prepayment posting") 
        ));
    }

    downloadAccrualPrepaymentStatement(companyId: string, startDate: string, endDate: string, yearId: number) {
        const url = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment/download`;
        const data = {
            startDate,
            endDate,
            yearId,
            reportType: 'Statement'
        };
        
        const linkHref = "accrualPrepaymentStatement.xlsx";
        return this.downloadExcelSpreadsheetPost(url, data,  linkHref);
        
    }

    downloadAccrualPrepaymentProjection(companyId: string, startDate: string, endDate: string, yearId: number) {
        const url = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment/download`;
        const data = {
            startDate,
            endDate,
            yearId,
            reportType: 'Projection'
        };
        
        const linkHref = "accrualPrepaymentProjection.xlsx";
        return this.downloadExcelSpreadsheetPost(url, data,  linkHref);
        
    }

    async getFinancialYears(companyId: string): Promise<IApiQueryResult>{
        let url: string = `${AppConfig.settings.apiFar}${companyId}/financialYear/`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapQueryFunction("An error occurred requesting the financial years"),
           this.getCatchErrorQueryFunction("An error occurred requesting the financial years") 
        ));
    }

    async getCompanyProcessingStatus(companyId: string): Promise<IApiResult>{
        let url: string = `${AppConfig.settings.apiFar}${companyId}/financialYear/GetCurrentYearProcessingStatus`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapFunction("An error occurred requesting the current year processing status"),
           this.getCatchErrorFunction("An error occurred requesting the current year processing status") 
        ));
    }

    async getAssetRegister(companyId: string, 
        assetGroupId: number,
        yearId: number,
        startDate: string,
        endDate: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/asset/getSummary`;
        const data = {
            assetGroupId,
            startDate,
            endDate,
            yearId
        };
        return await firstValueFrom(this.http.post<any>(url,data).pipe(
            this.getMapFunction("An error occurred retrieving the asset register"),
            this.getCatchErrorFunction("There was an error retrieving the asset register")
        ));
    }
    
    async getAssetRegisterAssets(companyId: string, 
        assetGroupId: number, 
        pageIndex: number, 
        pageSize: number, 
        yearId: number,
        startDate: string,
        endDate: string, 
        searchTerm?: string, 
        sortDesc?: boolean, 
        sortItem?: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/asset/getSummaryAssets`;
        const data = {
            assetGroupId,
            pageIndex,
            pageSize,
            searchTerm,
            sortDesc,
            sortItem,
            startDate,
            endDate,
            yearId
        };

        return await firstValueFrom(this.http.post<any>(url, data).pipe(
            this.getMapFunction("There was an error retrieving the assets for requested group"),
            this.getCatchErrorQueryFunction("There was an error retrieving the assets for requested group")
        ));
    }

    downloadAssetRegister(companyId: string, startDate: string, endDate: string, yearId: number) {
        const url = `${AppConfig.settings.apiFar}${companyId}/asset/download`;
        const data = {
            startDate,
            endDate,
            yearId
        };
        
        const linkHref = "fixedAssetRegister.xlsx";
        return this.downloadExcelSpreadsheetPost(url, data,  linkHref);
        
    }

    async getTransaction(transactionId: number, companyId: string): Promise<IApiResult>{
        const url = `${AppConfig.settings.apiFar}${companyId}/transaction/${transactionId}`;

        return await firstValueFrom(this.http.get<any>(url).pipe(
            this.getMapQueryFunction("There was an error getting the transaction"),
            this.getCatchErrorQueryFunction("There was an error getting the transaction")
        ));
    }

    async getTransactions(companyId: string,
        feature: string,
        state: string,
        pageIndex: number,
        pageSize: number,
        startDate: string,
        endDate: string) : Promise<IApiQueryResult>
    {
        const url = `${AppConfig.settings.apiFar}${companyId}/transaction`;
        const data = {
            feature,
            state,
            pageIndex,
            pageSize,
            startDate,
            endDate,
            sortDesc: false,
            searchTerm: ''
        };

        return await firstValueFrom(this.http.post<any>(url, data).pipe(
            this.getMapQueryFunction("There was an error retrieving the requested transactions"),
            this.getCatchErrorQueryFunction("There was an error retrieving the requested transactions")
        ));
    }

    async getTransactionsExternal(companyId: string) : Promise<IApiQueryResult>
    {
        const url = `${AppConfig.settings.apiFar}${companyId}/transaction/latest`;

        return await firstValueFrom(this.http.get<any>(url).pipe(
            this.getMapQueryFunction("There was an error refreshing external transactions"),
            this.getCatchErrorQueryFunction("There was an error refreshing external transactions")
        ));
    }

    async getTransactionsExternalV2(companyId: string, yearId: number) : Promise<IApiQueryResult>
    {
        const url = `${AppConfig.settings.apiFar}${companyId}/transaction/latest/${yearId}`;

        return await firstValueFrom(this.http.get<any>(url).pipe(
            this.getMapQueryFunction("There was an error refreshing external transactions for selected year"),
            this.getCatchErrorQueryFunction("There was an error refreshing external transactions for selected year")
        ));
    }

    async updateTransactionIgnored(id: number, companyId: string): Promise<IApiResult>{
        const url = `${AppConfig.settings.apiFar}${companyId}/transaction/ignored/${id}`;
        return firstValueFrom(this.http.post<any>(url, null).pipe(
            this.getMapFunction("There was an error setting the transaction as ignored"),
            this.getCatchErrorFunction("There was an error setting the transaction as ignored")
        ));
    }

    async updateTransactionRevisedIgnore(id: number, companyId: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/transaction/IgnoreUpdate/${id}`;
        return firstValueFrom(this.http.post<any>(url, null).pipe(
            this.getMapFunction("There was an error setting the revised transaction as ignored"),
            this.getCatchErrorFunction("There was an error setting the revised transaction as ignored")
        ));
    }

    async updateTransactionAsset(id: number, companyId: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/transaction/update/asset/${id}`;
        return firstValueFrom(this.http.post<any>(url, null).pipe(
            this.getMapFunction("There was an error updating the asset for the  transaction"),
            this.getCatchErrorFunction("There was an error updating the asset for the  transaction")
        ));
    }

    async updateTransactionPrepayment(id: number, companyId: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/transaction/update/prepayment/${id}`;
        return firstValueFrom(this.http.post<any>(url, null).pipe(
            this.getMapFunction("There was an error updating the prepayment for the  transaction"),
            this.getCatchErrorFunction("There was an error updating the prepayment for the  transaction")
        ));
    }

    async getAsset(id: number, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/asset/${id}`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapFunction("An error occurred retrieving the asset"),
           this.getCatchErrorFunction("An error occurred retrieving the asset") 
        ));
    }

    async getAssetsList(assetGroupId: number, companyId: string): Promise<IApiQueryResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/asset/list/${assetGroupId}`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapQueryFunction("An error occurred retrieving the asset list"),
           this.getCatchErrorQueryFunction("An error occurred retrieving the asset list") 
        ));
    }

    async getAssetsByState(state: string, pageIndex: number, pageSize: number, searchTerm: string, sortItem: string, sortDesc: boolean, companyId: string): Promise<IApiQueryResult> {
        const data = {
            pageIndex,
            pageSize,
            searchTerm,
            sortItem,
            sortDesc
        };
        const url: string = `${AppConfig.settings.apiFar}${companyId}/asset/state/${state}`;
        return await firstValueFrom(this.http.post<any>(url, data).pipe(
           this.getMapQueryFunction("An error occurred retrieving the asset list"),
           this.getCatchErrorQueryFunction("An error occurred retrieving the asset list") 
        ));
    }

    async getAssetsKitParents(assetGroupId: number, companyId: string): Promise<IApiQueryResult>{
        const url: string = `${AppConfig.settings.apiFar}${companyId}/asset/kits/${assetGroupId}`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapQueryFunction("An error occurred retrieving the kits parents asset list"),
           this.getCatchErrorQueryFunction("An error occurred retrieving the kit parents asset list") 
        ));
    }

    async getAssetsKitMembers(kitAssetId: number, companyId: string): Promise<IApiQueryResult>{
        const url: string = `${AppConfig.settings.apiFar}${companyId}/asset/members/${kitAssetId}`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapQueryFunction("An error occurred retrieving the kit members asset list"),
           this.getCatchErrorQueryFunction("An error occurred retrieving the kit members asset list") 
        ));
    }

    async updateAsset(id: number, asset: any, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/asset/${id}`;
        return await firstValueFrom(this.http.post<any>(url, asset).pipe(
            this.getMapFunction("An error occurred updating the asset data"),
            this.getCatchErrorFunction("An error occurred updating the asset data")
        ));
    }

    async getAssetForTransaction(id: number, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/asset/transaction/${id}`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapFunction("An error occurred retrieving the asset from the transaction"),
           this.getCatchErrorFunction("An error occurred retrieving the asset from the transaction") 
        ));
    }

    async updateAssetKit(id: number, kitAsset: any, newMembers: number[], removeMembers: number[], companyId: string): Promise<IApiResult>{
        const data = {
            kitAsset,
            newMembers,
            removeMembers
        };
        const url: string = `${AppConfig.settings.apiFar}${companyId}/asset/updateAssetKit/${id}`;
        return await firstValueFrom(this.http.post<any>(url, data).pipe(
           this.getMapFunction("An error occurred update the kit asset"),
           this.getCatchErrorFunction("An error occurred updating the kit asset") 
        ));
    }

    async updateKitRemoveAsset(memberId: number, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/asset/removeFromKit/${memberId}`;
        return await firstValueFrom(this.http.post<any>(url, null).pipe(
           this.getMapFunction("An error occurred removing the asset from kit"),
           this.getCatchErrorFunction("An error occurred removing the asset from kit")
        ));
    }

    async updateKitAddAsset(parentId: number, childId: number, companyId: string): Promise<IApiResult> {
        const data = {
            parentId,
            childId
        };
        const url: string = `${AppConfig.settings.apiFar}${companyId}/asset/addToKit`;
        return await firstValueFrom(this.http.post<any>(url, data).pipe(
           this.getMapFunction("An error occurred adding the asset to the kit"),
           this.getCatchErrorFunction("An error occurred adding the asset to the kit")
        ));
    }

    async assetConvertToKit(model: any, companyId: string): Promise<IApiResult>{
        const url: string =`${AppConfig.settings.apiFar}${companyId}/asset/convertAssetToKit`;
        return await firstValueFrom(this.http.post<any>(url, model).pipe(
           this.getMapFunction("There was an error converting this asset into a kit"),
           this.getCatchErrorFunction("There was an error converting this asset into a kit") 
        ));
    }

    //TODO: requires updating sepcifically in version 2
    async getAssetPeriods(id: number, companyId: string): Promise<IApiQueryResult> {
        const url: string =`${AppConfig.settings.apiFar}${companyId}/asset/periods/${id}`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapQueryFunction("There was an error retrieving asset posting periods"),
           this.getCatchErrorQueryFunction("There was an error retrieving asset posting periods") 
        ));
    }

    async getAssetPostings(id: number, companyId: string): Promise<IApiQueryResult> {
        const url: string =`${AppConfig.settings.apiFar}${companyId}/asset/postings/${id}`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapQueryFunction("There was an error retrieving asset postings"),
           this.getCatchErrorQueryFunction("There was an error retrieving asset postings") 
        ));
    }
    
    async updateAssetPosting(posting: any, id: number, companyId: string): Promise<IApiResult> {
        const url: string =`${AppConfig.settings.apiFar}${companyId}/asset/postings/${id}`;
        return await firstValueFrom(this.http.post<any>(url, posting).pipe(
           this.getMapQueryFunction("There was an error updating asset postings"),
           this.getCatchErrorQueryFunction("There was an error updating asset postings")
        ));
    }

    async deleteAssetPosting(id: number, companyId: string): Promise<IApiResult> {
        const url: string =`${AppConfig.settings.apiFar}${companyId}/asset/postings/${id}`;
        return await firstValueFrom(this.http.delete<any>(url).pipe(
           this.getMapQueryFunction("There was an error deleting asset posting"),
           this.getCatchErrorQueryFunction("There was an error deleting asset posting")
        ));
    }

    async getAssetTaxPostings(id: number, companyId: string): Promise<IApiQueryResult> {
        const url: string =`${AppConfig.settings.apiFar}${companyId}/asset/taxPostings/${id}`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapQueryFunction("There was an error retrieving asset tax postings"),
           this.getCatchErrorQueryFunction("There was an error retrieving asset tax postings") 
        ));
    }

    async updateAssetTaxPosting(posting: any, id: number, companyId: string): Promise<IApiResult> {
        const url: string =`${AppConfig.settings.apiFar}${companyId}/asset/taxPostings/${id}`;
        return await firstValueFrom(this.http.post<any>(url, posting).pipe(
           this.getMapQueryFunction("There was an error updating asset tax postings"),
           this.getCatchErrorQueryFunction("There was an error updating asset tax postings")
        ));
    }

    async deleteAssetTaxPosting(id: number, companyId: string): Promise<IApiResult> {
        const url: string =`${AppConfig.settings.apiFar}${companyId}/asset/taxPostings/${id}`;
        return await firstValueFrom(this.http.delete<any>(url).pipe(
           this.getMapQueryFunction("There was an error deleting asset tax posting"),
           this.getCatchErrorQueryFunction("There was an error deleting asset tax posting")
        ));
    }

    async updateAssetBulk(field: string, value: string, assets: number[], companyId: string): Promise<IApiResult> {
        const data = {
            field,
            value,
            assets
        };
        const url: string = `${AppConfig.settings.apiFar}${companyId}/asset/bulk`;
        return await firstValueFrom(this.http.post<any>(url, data).pipe(
           this.getMapFunction("An error occurred performing bulk edit"),
           this.getCatchErrorFunction("An error occurred performing bulk edit")
        ));
    }

    async updateAccrualPrepaymentsBulk(field: string, value: string, items: number[], companyId: string): Promise<IApiResult> {
        const data = {
            field,
            value,
            assets: items
        };
        const url: string = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment/bulk`;
        return await firstValueFrom(this.http.post<any>(url, data).pipe(
           this.getMapFunction("An error occurred performing bulk edit"),
           this.getCatchErrorFunction("An error occurred performing bulk edit")
        ));
    }

    async getBuildAssetForTransaction(id: number, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/transaction/build/asset/${id}`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapFunction("An error occurred building the asset from the transaction"),
           this.getCatchErrorFunction("An error occurred building the asset from the transaction") 
        ));
    }

    async createAssetForTransaction(id: number, asset: any, companyId: string):Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/transaction/asset/${id}`;
        return await firstValueFrom(this.http.post<any>(url, asset).pipe(
            this.getMapFunction("An error occurred creating the asset from the transaction"),
            this.getCatchErrorFunction("An error occurred creating the asset from the transaction")
        ));
    }

    async disposeAsset(model: any, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/asset/dispose`;
        return await firstValueFrom(this.http.post<any>(url, model ).pipe(
            this.getMapFunction("An error occurred attempting to dispose of the selected asset"),
            this.getCatchErrorFunction("An error occurred attempting to dispose of the selected asset")
        ));
    }

    async disposeAssetReverse(model: any, companyId: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/asset/ReverseDispose`;
        return await firstValueFrom(this.http.post<any>(url, model ).pipe(
            this.getMapFunction("An error occurred attempting to reverse disposal of the selected asset"),
            this.getCatchErrorFunction("An error occurred attempting to reverse disposal of the selected asset")
        ));
    }

    async deleteAssetPreflight(assetId: number, companyId: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/asset/deletePreflight/${assetId}`;
        return await firstValueFrom( this.http.get<any>(url).pipe(
            this.getMapFunction("An error occurred preflight checking this asset can be deleted"),
            this.getCatchErrorFunction("An error occurred preflight checking this asset can be deleted")
        ));
      }
    
    async deleteAsset(assetId: number, model: any, companyId: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/asset/delete/${assetId}`;
        return await firstValueFrom(this.http.post<any>(url, model).pipe(
            this.getMapFunction("An error occurred deleting this asset"),
            this.getCatchErrorFunction("An error occurred deleting this asset")
        ));
    }

    async deleteAssetReverse(assetId: number, companyId: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/asset/deleteReverse/${assetId}`;
        return await firstValueFrom(this.http.post<any>(url, null).pipe(
            this.getMapFunction("An error occurred deleting this asset"),
            this.getCatchErrorFunction("An error occurred deleting this asset")
        ));
    }

    async assetStartDepreciating(id:number, model: any, companyId: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/asset/depreciate/${id}`;
        return await firstValueFrom(this.http.post<any>(url, model).pipe(
            this.getMapFunction("An error occurred starting this asset depreciating"),
            this.getCatchErrorFunction("An error occurred starting this asset depreciating") 
        ));        
    }

    async getDepreciationRules(companyId: string): Promise<IApiQueryResult> {
        let url: string = `${AppConfig.settings.apiFar}${companyId}/depreciationRule/`;
        return await firstValueFrom(this.http.get<IApiQueryResult>(url).pipe(
           this.getMapQueryFunction("An error occurred requesting the depreciaton rules"),
           this.getCatchErrorQueryFunction("An error occurred requesting the depreciaton rules") 
        ));
    }

    async getDepreciationRule(id: number, companyId: string): Promise<IApiResult> {
        let url: string = `${AppConfig.settings.apiFar}${companyId}/depreciationRule/${id}`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapFunction("An error occurred requesting the depreciaton rule"),
           this.getCatchErrorFunction("An error occurred requesting the depreciaton rule") 
        ));
    }

    async updateDepreciationRule(id: number, data: any, companyId: string): Promise<IApiResult> {
        let url: string = `${AppConfig.settings.apiFar}${companyId}/depreciationRule/${id}`;
        return await firstValueFrom(this.http.post<any>(url, data).pipe(
           this.getMapFunction("An error occurred updating the depreciaton rule"),
           this.getCatchErrorFunction("An error occurred updating the depreciaton rule") 
        ));
    }

    async updateDepreciationRuleSetDefault(id: number, companyId: string): Promise<IApiResult> {
        let url: string = `${AppConfig.settings.apiFar}${companyId}/depreciationRule/default/${id}`;
        return await firstValueFrom(this.http.post<any>(url, null).pipe(
           this.getMapFunction("An error occurred setting the depreciaton rule to default"),
           this.getCatchErrorFunction("An error occurred setting the depreciaton rule to default") 
        ));
    }

    async deleteDepreciationRule(id: number, companyId: string): Promise<IApiResult> {
        let url: string = `${AppConfig.settings.apiFar}${companyId}/depreciationRule/${id}`;
        return await firstValueFrom(this.http.delete<any>(url).pipe(
           this.getMapFunction("An error occurred deleting the depreciaton rule"),
           this.getCatchErrorFunction("An error occurred deleting the depreciaton rule") 
        ));
    }

    async getNominalAccounts(companyId: string): Promise<IApiQueryResult> {
        let url: string = `${AppConfig.settings.apiFar}${companyId}/nominalaccount/`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapQueryFunction("An error occurred requesting the depreciaton rules"),
           this.getCatchErrorQueryFunction("An error occurred requesting the depreciaton rules") 
        ));
    }

    async getNominalAccount(id: number, companyId: string): Promise<IApiResult> {
        let url: string = `${AppConfig.settings.apiFar}${companyId}/nominalAccount/${id}`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapFunction("An error occurred requesting the nominal account"),
           this.getCatchErrorFunction("An error occurred requesting the nominal account") 
        ));
    }

    async updateNominalAccount(id:number, data: any, companyId: string): Promise<IApiResult> {
        let url: string = `${AppConfig.settings.apiFar}${companyId}/nominalaccount/${id}`;
        return await firstValueFrom(this.http.post<any>(url, data).pipe(
           this.getMapFunction("An error occurred setting the depreciaton rule to default"),
           this.getCatchErrorFunction("An error occurred setting the depreciaton rule to default") 
        ));
    }

    async getNominalAccountsExternal(companyId: string): Promise<IApiQueryResult> {
        let url: string = `${AppConfig.settings.apiFar}${companyId}/nominalaccount/External`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapQueryFunction("An error occurred requesting the depreciaton rules"),
           this.getCatchErrorQueryFunction("An error occurred requesting the depreciaton rules") 
        ));
    }

    async deleteNominalAccount(id: number, companyId: string): Promise<IApiResult> {
        let url: string = `${AppConfig.settings.apiFar}${companyId}/nominalAccount/${id}`;
        return await firstValueFrom(this.http.delete<any>(url).pipe(
           this.getMapFunction("An error occurred deleting the nominal account"),
           this.getCatchErrorFunction("An error occurred deleting the nominal account") 
        ));
    }

    async processPeriods(processDate: Date, companyId: string): Promise<IApiResult> {
        let url: string = `${AppConfig.settings.apiFar}${companyId}/financialYear/processperiod`;
        let model = { processDate: processDate };
        return await firstValueFrom(this.http.post<IApiResult>(url, model).pipe(
            this.getMapFunction("An error occurred requesting period end processing"),
            this.getCatchErrorFunction("An error occurred requesting period end processing")
        ));
    }

    async processYearEnd(model: any, companyId: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/financialYear/processEndOfYear`;
        return await firstValueFrom(this.http.post<IApiResult>(url, model).pipe(
            this.getMapFunction("An error occurred requesting year end processing"),
            this.getCatchErrorFunction("An error occurred requesting year end processing")
        ));
    }
    
    async processYearEndPreflight(companyId: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/financialYear/processEndOfYearPreflight`;
        return await firstValueFrom(this.http.post<IApiResult>(url, null).pipe(
            this.getMapFunction("An error occurred requesting preflight for year-end processing"),
            this.getCatchErrorFunction("An error occurred requesting preflight for year-end processing")
        ));
    }

    async processYearEndPreflightPerpetual(yeardId: number, companyId: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/financialYear/processEndOfYearPreflight/${yeardId}`;
        return await firstValueFrom(this.http.post<IApiResult>(url, null).pipe(
            this.getMapFunction("An error occurred requesting preflight for year-end processing"),
            this.getCatchErrorFunction("An error occurred requesting preflight for year-end processing")
        ));
    }

    async getCompany(companyId: string){
        const url = `${AppConfig.settings.apiFar}${companyId}/company`;
        return await firstValueFrom(this.http.get<IApiResult>(url).pipe(
            this.getMapFunction("An error occurred retrieving the company model"),
            this.getCatchErrorFunction("An error occurred retrieving the company model")
        ));
    }

    async getCompanyCanPost(companyId: string, option: CompanyPostingOptions): Promise<IApiResult> { 
        const url = `${AppConfig.settings.apiFar}${companyId}/company/canpost/${option}`;
        return await firstValueFrom(this.http.get<IApiResult>(url).pipe(
            this.getMapFunction("An error occurred confirming whether company is configured to make postings"),
            this.getCatchErrorFunction("An error occurred confirming whether company is configured to make postings")
        ));
    }

    async getFinancialYearDepreciation(yearId: number, groupdId: number, companyId: string): Promise<IApiQueryResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/financialYear/GetDepreciation/${yearId}/${groupdId}`;
        return await firstValueFrom(this.http.get<IApiQueryResult>(url).pipe(
            this.getMapQueryFunction("There was an error retrieving the depreciation projection for this group"),
            this.getCatchErrorQueryFunction("There was an error retrieving the depreciation projection for this group")
        ));
    }

    async getProjectionColumns(yearId: number, startDate: string, endDate: string, companyId: string, isTax: boolean): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/financialYear/GetProjectionColumns/`;
        const model = {
            yearId,
            startDate,
            endDate,
            isTax
        };
        return await firstValueFrom(this.http.post<IApiResult>(url, model).pipe(
            this.getMapFunction("There was an error retrieving the projection columns for this date range"),
            this.getCatchErrorFunction("There was an error retrieving the projection columns for this date range")
        ));
    }

    async getFinancialYearDepreciationAssets(yearId: number, startDate: string, endDate: string, assetGroupId: number, pageIndex: number, pageSize: number, searchTerm: string, companyId: string, sortItem: string, sortDesc: boolean, isTax: boolean): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/financialYear/GetDepreciationAssets/`;
        const model = {
            yearId,
            startDate,
            endDate,
            pageSize,
            pageIndex,
            assetGroupId,
            searchTerm,
            sortItem,
            sortDesc,
            isTax
        };
        return await firstValueFrom(this.http.post<IApiResult>(url, model).pipe(
            this.getMapFunction("There was an error retrieving the depreciation for assets in this group"),
            this.getCatchErrorFunction("There was an error retrieving the depreciation for assets in this group")
        ));
    }

    //This method will request the result if a report is requested
    async getFinancialYearDepreciationReportStatus(yearId: number, companyId: string): Promise<IApiResult>{
        const url = `${AppConfig.settings.apiFar}${companyId}/financialYear/projectionPreview/${yearId}`;
        return await firstValueFrom(this.http.get<IApiResult>(url).pipe(
            this.getMapFunction("There was an error confirming the status for your depreciation report"),
            this.getCatchErrorFunction("There was an error confirming the status for your depreciation report")
        ));
    }

    //This method should be used when Offline was the result of a status request
    //  as it will return the object received from the API
    async requestFinancialYearDeprecationReport(yearId: number, companyId: string): Promise<IApiResult>{
        const url = `${AppConfig.settings.apiFar}${companyId}/financialYear/projection`;
        const data = {
            startDate: null,
            endDate: null,
            yearId,
            isTax: false
        };
        return await firstValueFrom(this.http.post<IApiResult>(url, data).pipe(
            this.getMapFunction("There was an error requesting your depreciation report"),
            this.getCatchErrorFunction("There was an error requesting your depreciation report")
        ));
    }

    //This method should be used when Download was the result of a status request
    //  as it will generate a download link from the binary result
    downloadFinancialYearDepreciationReport(yearId: number, startDate: string, endDate: string, companyId: string, isTax: boolean): Observable<boolean> {
        const url = `${AppConfig.settings.apiFar}${companyId}/financialYear/projection`;
        const data = {
            startDate,
            endDate,
            yearId,
            isTax
        };
        const linkHref = `depreciationReport.xlsx`;

        return this.downloadExcelSpreadsheetPost(url, data, linkHref);
    }



    importAssetsFileUpload(file: any, companyId: string) {
        const formData: FormData = new FormData();
        formData.append('file', file, file.name);
        let url: string = `${AppConfig.settings.apiFar}${companyId}/asset/upload`
        // create a http-post request and pass the form
        // tell it to report the upload progress
        const req = new HttpRequest('POST', url, formData, {
            reportProgress: true
        });
    
        // create a new progress-subject for every file
        const progress = new Subject<number>();
        const assetData = new Subject<IApiResult>();
    
        // send the http-request and subscribe for progress-updates
        this.http.request<IApiResult>(req).pipe(
            catchError((e) => {
                progress.complete();
                assetData.next(null); 
                return null;
            })
        ).subscribe((event:any) => {
            if(!event){
                return;
            }

            if (event.type === HttpEventType.UploadProgress) {
                // calculate the progress percentage
                const percentDone = Math.round(100 * event.loaded / event.total);
                // pass the percentage into the progress-stream
                progress.next(percentDone);
            } else if (event instanceof HttpResponse) {
                // Close the progress-stream if we get an answer form the API
                // The upload is complete
                assetData.next(event.body);
                progress.complete();
            }
        });
    
        return {
            progress: progress,
            data: assetData
        };
    }

    async importAssets(data: any, companyId: string): Promise<IApiQueryResult>{
        const url = `${AppConfig.settings.apiFar}${companyId}/asset/import`;
        return await firstValueFrom(this.http.post<IApiQueryResult>(url, data).pipe(
            this.getMapQueryFunction("There was an error importing your assets"),
            this.getCatchErrorQueryFunction("There was an error importing your assets")
        ));
    }

    importAcrrualsFileUpload(file: any, companyId: string) {
        const formData: FormData = new FormData();
        formData.append('file', file, file.name);
        let url: string = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment/upload`
        // create a http-post request and pass the form
        // tell it to report the upload progress
        const req = new HttpRequest('POST', url, formData, {
            reportProgress: true
        });
    
        // create a new progress-subject for every file
        const progress = new Subject<number>();
        const assetData = new Subject<IApiResult>();
    
        // send the http-request and subscribe for progress-updates
        this.http.request<IApiResult>(req).pipe(
            catchError((e) => {
                progress.complete();
                assetData.next(null); 
                return null;
            })
        ).subscribe((event:any) => {
            if(!event){
                return;
            }

            if (event.type === HttpEventType.UploadProgress) {
                // calculate the progress percentage
                const percentDone = Math.round(100 * event.loaded / event.total);
                // pass the percentage into the progress-stream
                progress.next(percentDone);
            } else if (event instanceof HttpResponse) {
                // Close the progress-stream if we get an answer form the API
                // The upload is complete
                assetData.next(event.body);
                progress.complete();
            }
        });
    
        return {
            progress: progress,
            data: assetData
        };
    }

    async importAccrualPrepayments(data: any, companyId: string): Promise<IApiResult>{
        const url = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment/import`;
        return await firstValueFrom(this.http.post<IApiResult>(url, data).pipe(
            this.getMapFunction("There was an error importing your accrual prepayment"),
            this.getCatchErrorFunction("There was an error importing your accrual prepayment")
        ));
    }

    async getAccrualPrepaymentPeriods(id: number, companyId: string): Promise<IApiQueryResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/accrualPrepayment/periods/${id}`;
        return await firstValueFrom(this.http.get<IApiQueryResult>(url).pipe(
            this.getMapQueryFunction("There was an error retrieving the prepayment periods"),
            this.getCatchErrorQueryFunction("There was an error retrieving the prepayment periods")
        ));
    }

    async updateAssetAdjustmentPeriod(id: number, value: number, companyId: string): Promise<IApiResult> {
        const model = {
            assetPeriodId: id,
            newValue: value
        };
        const url = `${AppConfig.settings.apiFar}${companyId}/FinancialYear/UpdateAssetAdjustmentPeriod`;
        return await firstValueFrom(this.http.post<IApiResult>(url, model).pipe(
            this.getMapFunction("There was an error retrieving the prepayment periods"),
            this.getCatchErrorFunction("There was an error retrieving the prepayment periods")
        ));
    }

    async getJournalEntries(pageIndex: number, pageSize: number, companyId: string): Promise<IApiQueryResult>{
        const url = `${AppConfig.settings.apiFar}${companyId}/JournalEntry/${pageIndex}/${pageSize}`;
        return await firstValueFrom(this.http.get<IApiQueryResult>(url).pipe(
            this.getMapFunction("There was an error retrieving the journal entries"),
            this.getCatchErrorQueryFunction("There was an error retrieving the journal entries")
        ));
    }

    async getJournalEntriesForPeriod(reportingPeriodId: number, companyId: string): Promise<IApiQueryResult>{
        const url = `${AppConfig.settings.apiFar}${companyId}/JournalEntry/${reportingPeriodId}`;
        return await firstValueFrom(this.http.get<IApiQueryResult>(url).pipe(
            this.getMapFunction("There was an error retrieving the journal entries for reporting period"),
            this.getCatchErrorQueryFunction("There was an error retrieving the journal entries for reporting period")
        ));
    }

    async getJournalEntry(journalEntryId: number, companyId: string): Promise<IApiResult>{
        const url = `${AppConfig.settings.apiFar}${companyId}/JournalEntry/GetEntry/${journalEntryId}`;
        return await firstValueFrom(this.http.get<IApiQueryResult>(url).pipe(
            this.getMapFunction("There was an error retrieving the journal entries"),
            this.getCatchErrorQueryFunction("There was an error retrieving the journal entries")
        ));
    }

    async getJournalEntryLines(journalEntryId: number, pageIndex: number, pageSize: number, companyId: string): Promise<IApiQueryResult>{
        const url = `${AppConfig.settings.apiFar}${companyId}/JournalEntry/GetEntryLines/${journalEntryId}/${pageIndex}/${pageSize}`;
        return await firstValueFrom(this.http.get<IApiQueryResult>(url).pipe(
            this.getMapFunction("There was an error retrieving the journal entry lines"),
            this.getCatchErrorQueryFunction("There was an error retrieving the journal entry lines")
        ));
    }

    async getJournalPostings(journalId: number, companyId: string): Promise<IApiQueryResult>{
        const url = `${AppConfig.settings.apiFar}${companyId}/JournalEntry/postings/${journalId}`;
        return await firstValueFrom(this.http.get<IApiQueryResult>(url).pipe(
            this.getMapFunction("There was an error retrieving the journal posting for journal"),
            this.getCatchErrorQueryFunction("There was an error retrieving the journal postings for journal")
        ));
    }

    async updateProviderJournalLine(id: number, drAccountId: number, crAccountId: number, companyId: string): Promise<IApiResult>{
        const url = `${AppConfig.settings.apiFar}${companyId}/JournalEntry/UpdateLine/${id}/${drAccountId}/${crAccountId}`;
        return await firstValueFrom(this.http.post<IApiResult>(url, null).pipe(
            this.getMapFunction("There was an error updating the journal line accounts"),
            this.getCatchErrorFunction("There was an error updating the journal line accounts")
        ));
    }

    async updateProviderJournalDate(journalId: number, journalDate: any, companyId: string): Promise<IApiResult>{
        const url = `${AppConfig.settings.apiFar}${companyId}/JournalEntry/UpdateDate/${journalId}/${journalDate}`;
        return await firstValueFrom(this.http.post<IApiResult>(url, null).pipe(
            this.getMapFunction("There was an error updating the journal date"),
            this.getCatchErrorFunction("There was an error updating the journal date")
        ));
    }

    async getJournalErrors(companyId: string): Promise<IApiQueryResult>{
        const url = `${AppConfig.settings.apiFar}${companyId}/JournalEntry/errors`;
        return await firstValueFrom(this.http.get<IApiQueryResult>(url).pipe(
            this.getMapFunction("There was an error retrieving the journal errors"),
            this.getCatchErrorQueryFunction("There was an error retrieving the journal errors")
        ));
    }
    
    async repostJournalEntry(journalId: number, companyId: string): Promise<IApiQueryResult>{
        const url = `${AppConfig.settings.apiFar}${companyId}/JournalEntry/repost/${journalId}`;
        return await firstValueFrom(this.http.get<IApiQueryResult>(url).pipe(
            this.getMapFunction("There was an error reposting the journal entry"),
            this.getCatchErrorQueryFunction("There was an error reposting the journal entry")
        ));
    }
    async resolveJournalEntry(journalId: number, companyId: string): Promise<IApiQueryResult>{
        const url = `${AppConfig.settings.apiFar}${companyId}/JournalEntry/resolve/${journalId}`;
        return await firstValueFrom(this.http.get<IApiQueryResult>(url).pipe(
            this.getMapFunction("There was an error resolving the journal entry"),
            this.getCatchErrorQueryFunction("There was an error resolving the journal entry")
        ));
    }

    async updateJournalEntry(id: number, journalRef: string, journalDate: any, companyId: string): Promise<IApiResult> {
        const url = `${AppConfig.settings.apiFar}${companyId}/JournalEntry/${id}`;
        const data = {
            journalRef,
            journalDate
        };

        return await firstValueFrom(this.http.post<IApiResult>(url, data).pipe(
            this.getMapFunction("There was an error updating the journal entry"),
            this.getCatchErrorQueryFunction("There was an error updating the journal entry")
        ));
        
    }

    async getLoanItem(id: number, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/loan/${id}`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
            this.getMapFunction("An error occurred retrieving the loan item data"),
            this.getCatchErrorFunction("An error occurred retrieving the loan item data")
        ));
    }

    async getLoanTypeCount(companyId: string): Promise<IApiQueryResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/loan/loantypecount`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
            this.getMapQueryFunction("An error occurred retrieving the loan type count data"),
            this.getCatchErrorQueryFunction("An error occurred retrieving the loan type count data")
        ));
    }

    async getLoanItems(companyId: string): Promise<IApiQueryResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/loan/`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
            this.getMapQueryFunction("An error occurred retrieving the loan items data"),
            this.getCatchErrorQueryFunction("An error occurred retrieving the loan items data")
        ));
    }

    async getLoanItemPostings(id: number, companyId: string): Promise<IApiQueryResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/loan/postings/${id}`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
            this.getMapQueryFunction("An error occurred retrieving the loan item posting data"),
            this.getCatchErrorQueryFunction("An error occurred retrieving the loan item posting data")
        ));
    }

    async updateLoanItem(loanItem: any, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/loan/`;
        return await firstValueFrom(this.http.post<any>(url, loanItem).pipe(
            this.getMapFunction("An error occurred updating the loan item data"),
            this.getCatchErrorFunction("An error occurred updating the loan item data")
        ));
    }

    async getLoanStatement(data: any, companyId: string): Promise<IApiQueryResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/loan/statement`;
        return await firstValueFrom(this.http.post<any>(url, data).pipe(
           this.getMapQueryFunction("An error occurred retrieving the loan statement"),
           this.getCatchErrorQueryFunction("An error occurred retrieving the loan statement") 
        ));
    }

    async getLoanAPR(data: any, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/loan/getapr`;
        return await firstValueFrom(this.http.post<any>(url, data).pipe(
           this.getMapFunction("An error occurred retrieving the loan APR"),
           this.getCatchErrorFunction("An error occurred retrieving the loan APR") 
        ));
    }

    async getLoanProjection(data: any, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/loan/projection`;
        return await firstValueFrom(this.http.post<any>(url, data).pipe(
           this.getMapFunction("An error occurred retrieving the loan projection"),
           this.getCatchErrorFunction("An error occurred retrieving the loan projection") 
        ));
    }

    async getLoansDeleted(companyId: string): Promise<IApiQueryResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/loan/deleted`;
        return await firstValueFrom(this.http.get<any>(url).pipe(
           this.getMapQueryFunction("An error occurred retrieving deleted loans"),
           this.getCatchErrorQueryFunction("An error occurred retrieving deleted loans") 
        ));
    }

    async deleteLoan(model: any, companyId: string): Promise<IApiResult> {
       const url: string = `${AppConfig.settings.apiFar}${companyId}/loan/delete`;
        return await firstValueFrom(this.http.post<any>(url, model).pipe(
           this.getMapFunction("An error occurred deleting the loan"),
           this.getCatchErrorFunction("An error occurred deleting the loan") 
        ));
    }

    async deleteLoanReversal(id: number, companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/loan/deletereversal/${id}`;
        return await firstValueFrom(this.http.post<any>(url, null).pipe(
           this.getMapFunction("An error occurred deleting the loan"),
           this.getCatchErrorFunction("An error occurred deleting the loan") 
        ));
    }

    async loanNominalBulkUpdate(items: any[], companyId: string): Promise<IApiResult> {
        const url: string = `${AppConfig.settings.apiFar}${companyId}/loan/nominal`;
        return await firstValueFrom(this.http.post<any>(url, items).pipe(
           this.getMapQueryFunction("An error occurred bulk nominal updating loans"),
           this.getCatchErrorQueryFunction("An error occurred bulk nominal updating loans") 
        ));
    }

    downloadLoanStatement(companyId: string, startDate: string, endDate: string, yearId: number) {
        const url = `${AppConfig.settings.apiFar}${companyId}/loan/download`;
        const data = {
            startDate,
            endDate,
            yearId,
            reportType: 'Statement'
        };
        
        const linkHref = "loanProjection.xlsx";
        return this.downloadExcelSpreadsheetPost(url, data,  linkHref);
        
    }

    downloadLoanProjection(companyId: string, startDate: string, endDate: string, yearId: number) {
        const url = `${AppConfig.settings.apiFar}${companyId}/loan/download`;
        const data = {
            startDate,
            endDate,
            yearId,
            reportType: 'Projection'
        };
        
        const linkHref = "loanProjection.xlsx";
        return this.downloadExcelSpreadsheetPost(url, data,  linkHref);
        
    }
}
